import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { isEmpty, pickBy } from 'lodash';
import { Company, FirmCompany, FirmsService } from 'api';
import {
  CREATE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE,
  CREATE_FUND_INVESTMENT_DEFAULT_SUCCESS_MESSAGE,
  FUND_INVESTMENT_SERVICES_ERROR_MESSAGES,

} from 'common/constants/services/fundInvestments';
import { SuperAgentError } from 'common/types/services';
import { getNumberValue, getStringValue, throwError } from 'utilities';
import {
  CreateFundInvestmentParams,
  CreateFundInvestmentResponse,
  UseCreateFundInvestmentReturn,
} from './useCreateFundInvestment.types';

const createFundInvestment = async (
  params: CreateFundInvestmentParams
): Promise<CreateFundInvestmentResponse | null> => {
  const { fundInvestment } = params;

  if (isEmpty(pickBy(fundInvestment))) {
    return throwError(CREATE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const createdFundInvestment = (await FirmsService.apiFirmsCompaniesNewCreate(
      fundInvestment as unknown as FirmCompany // The value of fiscal_year_end on FirmCompany is not accurately typed in the API
    )) as Company;

    if (createdFundInvestment?.id) {
      return {
        fundInvestment: createdFundInvestment,
        responseMessage: CREATE_FUND_INVESTMENT_DEFAULT_SUCCESS_MESSAGE(getStringValue(createdFundInvestment?.name)),
      } as CreateFundInvestmentResponse;
    }

    return throwError(CREATE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE);
  } catch (error) {
    const { status } = error as SuperAgentError;

    return throwError(
      FUND_INVESTMENT_SERVICES_ERROR_MESSAGES?.[getNumberValue(status)] ?? CREATE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE,
      error
    );
  }
};

const useCreateFundInvestment = (): UseCreateFundInvestmentReturn => {
  const queryClient = useQueryClient();

  const createFundInvestmentMutation: UseMutationResult<
    CreateFundInvestmentResponse | null, // Response type (null if error)
    Error, // Error type
    CreateFundInvestmentParams // Parameters type
  > = useMutation({
    mutationFn: createFundInvestment,
  });

  const { isSuccess, isError, mutateAsync } = createFundInvestmentMutation;

  if (isSuccess) {
    // Invalidate queries to refetch the data after mutation success
    queryClient.invalidateQueries({ queryKey: [] });

    // Clean the mutation internal state
    createFundInvestmentMutation.reset();
  }

  return {
    isError,
    mutateAsync,
  };
};

export default useCreateFundInvestment;
